/*
  tw refers to Template and Window
  This file is purely to get values stored in window and html template.
 */

import _isEmpty from 'lodash/isEmpty';
import _toNumber from 'lodash/toNumber';
import { getContent } from 'pp-react-l10n';
import { contactOptionIcons, experiences } from '../common/constants';
import { getLocationPath } from './globalHelper';
import seoURlFilter from '../../data/seoURLFilters.json';
import contentHelper from '../../js/lib/contentHelper';
import { getOriginPath } from '../../js/lib/globalHelper';
import getFormattedURIForArticle from '../../js/minihelp/lib/urlHelpers';

/**
 *  This will return the value from document using the key
 * @param key
 * @returns {string|null}
 */
export const getValueFromHtmlDocument = (key) => {
  try {
    const html = document.querySelector('html');
    return html.getAttribute(key);
  } catch (e) {
    return null;
  }
};

/**
 * This will return true if attribute exists
 * @param key
 * @returns {true|false}
 */
export const hasValueFromHtmlDocument = (key) => {
  try {
    const html = document.querySelector('html');
    return html.hasAttribute(key);
  } catch (e) {
    return false;
  }
};

/**
 *  This will return the value of the key from the window
 * @param key
 * @returns {null|*}
 */
export const getValueFromWindowObject = (key) => {
  try {
    const appConfig = window && window.shAppConfig;
    return appConfig[key];
  } catch (e) {
    return null;
  }
};

export const getValueFromGenericWindowObject = (key) => {
  try {
    return window && window[key];
  } catch (e) {
    return null;
  }
};

/**
 *
 * @param key
 * @returns {null|*}
 */
export const getValueFromDocumentAttribute = (key) => {
  try {
    return document.body.getAttribute(key);
  } catch (e) {
    return null;
  }
};

/**
 * Function to get timeout for loader from Remote config
 */
export const getLoaderTimeout = () => {
  try {
    const appConfig = getValueFromWindowObject('appConfig') || {};
    return appConfig.globalLoaderTimeOut || 2000;
  } catch (e) {
    return 2000;
  }
};

export const getDirectionality = () => getValueFromHtmlDocument('dir') || 'ltr';

export const getCoBrand = () => getValueFromHtmlDocument('cobrand') || 'us';

export const getExtendedFooterDataFromTW = () => getValueFromWindowObject('extendedFooterData') || [];

export const getDeviceType = () => getValueFromDocumentAttribute('data-device-type');

export const isMobileLayout = () => getDeviceType() === 'mobile';

export const isLoggedIn = () => getValueFromDocumentAttribute('isloggedin') === 'true';

export const isTabletLayout = () => getValueFromHtmlDocument('data-device-type') === 'tablet';

export const getUserProfile = () => getValueFromWindowObject('userProfile');

export const getCsrfToken = () => getValueFromDocumentAttribute('data-token');

export const getCurrentPageName = () => getValueFromHtmlDocument('page') || '';

export const getExperience = () => getValueFromHtmlDocument('experience') || experiences.PERSONAL;

export const isHomePage = () => getCurrentPageName() === 'home';

export const isContactPage = () => getCurrentPageName() === 'contact-us';

export const isNewHomeExpEnabled = () => isLoggedIn() && getValueFromWindowObject('isNewHomeExpEnabled') === true;

export const isNewContactUsExpEnabled = () => getValueFromWindowObject('isNewContactUsExpEnabled') === true;

export const isTechnicalExperience = () => getExperience() === experiences.TECHNICAL;

export const getLanguage = () => getValueFromHtmlDocument('language') || 'en';

export const isNewExpLayoutEnabled = () =>
  isLoggedIn() && !isTechnicalExperience() && getValueFromWindowObject('isNewExpLayoutEnabled') === true;

// Function to return IcfConfig Mappings from windows obj
export const getIcfConfigMappings = () => getValueFromGenericWindowObject('icfConfig')?.mapping || {};

export const isCommunityEnabled = () => {
  const community = getValueFromWindowObject('community');
  return community.isEnabled || false;
};

export const getCommunityUrl = () => {
  const community = getValueFromWindowObject('community');
  return (community && community.communityUrl) || null;
};

export const isBusinessUser = () => getValueFromWindowObject('isBusinessUser');

export const getQualtricsConfig = () => getValueFromGenericWindowObject('qualtricsFeedbackConfig') || {};

/**
 * show Authorization Confirmation StepUp Prompt - yes/no
 */
export const isShowAuthConfirmStepUpPrompt = () =>
  getValueFromHtmlDocument('showLoginConfirmStepUp') === 'true' && isLoggedIn();

export const getStepUpUUID = () => getValueFromHtmlDocument('confirmloginstepupid') || '';

/**
 * Function to get isCustomerjourneyAPIEnabled RC flag from omnichannel config
 */
export const isCustomerJourneyAPIRCEnabled = () => {
  const appConfig = getValueFromWindowObject('appConfig') || {};
  const omniConfig = appConfig.omnichannel_config || {};
  return omniConfig.isCustomerJourneyEnabled || false;
};

export const getContactOptions = () => getValueFromGenericWindowObject('contactChannels');

export const joinContactOptions = (channels = []) => {
  if (_isEmpty(channels)) return '';

  const sortedChannels = [...channels].sort(
    (channel1, channel2) => _toNumber(channel1.priority) - _toNumber(channel2.priority),
  );

  return sortedChannels.map((channel) => channel.type).join('|');
};

export const joinTopicIntents = (intents = []) => {
  if (_isEmpty(intents)) return '';

  return intents.map((intent) => intent.ref_id).join('|');
};

export const getCountry = () => {
  return getCoBrand();
};

export const filterURLWordSEO = (articleTitle) => {
  try {
    if (getValueFromHtmlDocument('seoURLFilterWords')) {
      // Will remove special character from the string to filter out words
      const specialCharactersToReplace = /[?.,!{}()"-]/g; // Add characters here to remove it from the article
      const seoWordsLowerCase = seoURlFilter.filterWords.map((item) => item.toLowerCase());
      return articleTitle
        .replace(specialCharactersToReplace, '')
        .toLowerCase()
        .split(' ')
        .filter((item) => !seoWordsLowerCase.includes(item))
        .join(' ');
    }
    return articleTitle;
  } catch (e) {
    return articleTitle;
  }
};

export const getFaqUrlFromTitle = (title) => {
  return encodeURIComponent(
    title && filterURLWordSEO(title).replace(/ /g, '-').replace(/\?/g, '').replace(/[/\\]/g, '-').toLowerCase(),
  );
};

export const getArticleLink = (faqId, faqTitle = '') => {
  const country = getCountry();
  const articleTitle = faqTitle ? `${getFaqUrlFromTitle(faqTitle)}-` : '';
  return decodeURIComponent(`/${country}/smarthelp/article/${articleTitle}${faqId.toLowerCase()}`);
};

export const getArticleLinkForHref = (faq) => {
  const uri = getFormattedURIForArticle(faq);
  const articleUrl = `/${getCoBrand()}/smarthelp/article/${uri}`;
  return articleUrl;
};

export const getChatBotEligibilityParams = (intent, intentType) => {
  const path = getLocationPath().split('/').pop() || null;
  const intentId = intent || path;
  return {
    intentId,
    intentType,
    entryPoint: path,
  };
};

export const isChatMobileTabletDisable = () => getValueFromHtmlDocument('chatbot_mobile_tablet_disable');

export const getBaseURLWithExperience = () => `${getCoBrand()}/smarthelp`;

export const getHomeURL = () => {
  return decodeURIComponent(`/${getBaseURLWithExperience()}/home`);
};

export const channelsFilterForCommunity = (channel) =>
  channel !== contactOptionIcons.COMMUNITY || (channel === contactOptionIcons.COMMUNITY && isCommunityEnabled());

export const getResolutionURL = () =>
  contentHelper('/signin/?returnUri={originURL}/disputes', { originURL: encodeURIComponent(getOriginPath()) });

export const getReturnURLAfterLogin = () =>
  contentHelper('/signin/?returnUri={originURL}', { originURL: encodeURIComponent(window.location.pathname.slice(3)) });

export const handlePostChatBotEligibility = (intentCode, type, checkPostChatBotEligibility) => {
  const postChatBotResponse = getChatBotEligibilityParams(intentCode, type);
  checkPostChatBotEligibility(postChatBotResponse);
};

export const getHTMLElement = (options) => {
  return getContent({
    content: {
      innerMessage: options.message,
    },
    path: 'innerMessage',
    useHTML: options.useHTML || false,
  });
};

export const getCurrentLocationUrl = () => window.location.href;

export const isMobileAuthWebStepUpEnabledCountries = () =>
  hasValueFromHtmlDocument('isMobileauthwebStepupEnabledCountries');
export const isMobileWebCallUsLinkEnabled = () => hasValueFromHtmlDocument('isMobileWebCallUsLinkEnabled');

/**
 * Remote config for complaints Data
 */
export const getComplaintsData = () => getValueFromWindowObject('complaintsData') || {};

/*
 * check Url path if path ends with /contact-us or /contact-us/
 * */
export const checkUrlEndsWithContactUs = (pathName) =>
  pathName.endsWith('/contact-us') || pathName.endsWith('/contact-us/');

export const getPathName = () => window && window.location && window.location.pathname;
